package fr.manyverse.app.presentation.dice3d

import androidx.compose.ui.geometry.CornerRadius
import androidx.compose.ui.geometry.Rect
import androidx.compose.ui.geometry.RoundRect
import androidx.compose.ui.graphics.Matrix
import androidx.compose.ui.graphics.Path
import androidx.compose.ui.graphics.vector.PathParser
import fr.manyverse.app.domain.model.DiceFaceStyle

/**
 * Glyphe vectoriel en espace unité [-1, 1]², dessiné dans le repère projeté de la face
 * (voir `Dice3DCanvas.drawFaceGlyph`). Tout est tracé : les glyphes texte (☠, ⚡…) ne sont
 * pas fiables d'une plateforme à l'autre.
 */
internal class OneFaceGlyph(
    /** Silhouette, peinte dans la couleur des chiffres. */
    val fill: Path,
    /** Creux repeints par-dessus dans la couleur de la face. Null quand [fill] les porte déjà. */
    val hollows: Path? = null,
    /** Décalage du centre optique du glyphe dans son espace unité. */
    val centerOffsetY: Float = 0f
)

/**
 * Image portée par la face « 1 » des dés 3D. Préférence cosmétique globale de l'utilisateur,
 * persistée comme [DiceColor] et la couleur du feutre — le style de face de l'univers
 * ([DiceFaceStyle], choisi par le MJ) ne sert plus que de valeur initiale, cf. [fromFaceStyle].
 */
enum class DiceOneFace(val id: String) {
    /** Pas d'image : le 1 s'écrit en chiffre, comme les autres faces. */
    NONE("none"),
    SKULL("skull"),
    BOMB("bomb"),
    SWORDS("swords"),
    LIGHTNING("lightning"),
    FLAME("flame"),
    POISON("poison");

    internal val glyph: OneFaceGlyph?
        get() = when (this) {
            NONE -> null
            SKULL -> OneFaceGlyphs.skull
            BOMB -> OneFaceGlyphs.bomb
            SWORDS -> OneFaceGlyphs.swords
            LIGHTNING -> OneFaceGlyphs.lightning
            FLAME -> OneFaceGlyphs.flame
            POISON -> OneFaceGlyphs.poison
        }

    companion object {
        val DEFAULT = NONE

        /**
         * Null — et non [DEFAULT] — quand l'identifiant est inconnu ou absent : c'est ce qui
         * distingue « l'utilisateur n'a rien choisi » de « l'utilisateur a choisi aucune image ».
         */
        fun fromId(id: String?): DiceOneFace? = entries.firstOrNull { it.id == id }

        /** Valeur initiale déduite du réglage d'univers, tant que l'utilisateur n'a rien choisi. */
        fun fromFaceStyle(faceStyle: DiceFaceStyle): DiceOneFace =
            if (faceStyle == DiceFaceStyle.SKULL_ONE) SKULL else DEFAULT
    }
}

/**
 * Tracés des glyphes, construits une seule fois. La tête de mort est dessinée à la main
 * (calotte + mâchoire, puis creux repeints) ; les autres viennent de Material Design Icons,
 * comme les `icon_*.xml` de `composeResources` — leurs trous sont déjà portés par le sens de
 * parcours du tracé (remplissage non-zero), d'où un `hollows` nul.
 */
private object OneFaceGlyphs {

    val skull = OneFaceGlyph(
        fill = Path().apply {
            // Crâne (calotte ovale) + mâchoire (rectangle arrondi).
            addOval(Rect(left = -0.62f, top = -0.85f, right = 0.62f, bottom = 0.35f))
            addRoundRect(
                RoundRect(
                    Rect(left = -0.34f, top = 0.05f, right = 0.34f, bottom = 0.68f),
                    CornerRadius(0.16f)
                )
            )
        },
        hollows = Path().apply {
            // Yeux.
            addOval(Rect(left = -0.42f, top = -0.48f, right = -0.10f, bottom = -0.10f))
            addOval(Rect(left = 0.10f, top = -0.48f, right = 0.42f, bottom = -0.10f))
            // Nez (triangle inversé).
            moveTo(0f, 0.02f)
            lineTo(-0.10f, 0.24f)
            lineTo(0.10f, 0.24f)
            close()
            // Fentes des dents.
            addRoundRect(
                RoundRect(Rect(left = -0.155f, top = 0.38f, right = -0.095f, bottom = 0.66f), CornerRadius(0.03f))
            )
            addRoundRect(
                RoundRect(Rect(left = -0.03f, top = 0.38f, right = 0.03f, bottom = 0.66f), CornerRadius(0.03f))
            )
            addRoundRect(
                RoundRect(Rect(left = 0.095f, top = 0.38f, right = 0.155f, bottom = 0.66f), CornerRadius(0.03f))
            )
        },
        centerOffsetY = -0.08f
    )

    /**
     * MDI `bomb`, amputé des trois sous-tracés de l'étincelle : à la taille d'une face de dé
     * ils ne sont que des poussières, et ils décentraient la bombe.
     */
    val bomb = glyphFromPathData(
        "M11.25,6A3.25,3.25 0 0,1 14.5,2.75A3.25,3.25 0 0,1 17.75,6C17.75,6.42 18.08,6.75 18.5," +
            "6.75C18.92,6.75 19.25,6.42 19.25,6V5.25H20.75V6A2.25,2.25 0 0,1 18.5,8.25A2.25,2.25 " +
            "0 0,1 16.25,6A1.75,1.75 0 0,0 14.5,4.25A1.75,1.75 0 0,0 12.75,6H14V7.29C16.89,8.15 " +
            "19,10.83 19,14A7,7 0 0,1 12,21A7,7 0 0,1 5,14C5,10.83 7.11,8.15 10,7.29V6H11.25Z"
    )

    /** MDI `sword-cross`. */
    val swords = glyphFromPathData(
        "M6.2,2.44L18.1,14.34L20.22,12.22L21.63,13.63L19.16,16.1L22.34,19.28C22.73,19.67 22.73," +
            "20.3 22.34,20.69L21.63,21.4C21.24,21.79 20.61,21.79 20.22,21.4L17,18.23L14.56,20.7L13.15," +
            "19.29L15.27,17.17L3.37,5.27V2.44H6.2M15.89,10L20.63,5.26V2.44H17.8L13.06,7.18L15.89," +
            "10M10.94,15L8.11,12.13L5.9,14.34L3.78,12.22L2.37,13.63L4.84,16.1L1.66,19.29C1.27,19.68 " +
            "1.27,20.31 1.66,20.7L2.37,21.41C2.76,21.8 3.39,21.8 3.78,21.41L7,18.23L9.44,20.7L10.85," +
            "19.29L8.73,17.17L10.94,15Z"
    )

    /** MDI `flash`. */
    val lightning = glyphFromPathData("M7,2V13H10V22L17,10H13L17,2H7Z")

    /** MDI `fire`. */
    val flame = glyphFromPathData(
        "M17.66 11.2C17.43 10.9 17.15 10.64 16.89 10.38C16.22 9.78 15.46 9.35 14.82 8.72C13.33 " +
            "7.26 13 4.85 13.95 3C13 3.23 12.17 3.75 11.46 4.32C8.87 6.4 7.85 10.07 9.07 13.22C9.11 " +
            "13.32 9.15 13.42 9.15 13.55C9.15 13.77 9 13.97 8.8 14.05C8.57 14.15 8.33 14.09 8.14 " +
            "13.93C8.08 13.88 8.04 13.83 8 13.76C6.87 12.33 6.69 10.28 7.45 8.64C5.78 10 4.87 12.3 " +
            "5 14.47C5.06 14.97 5.12 15.47 5.29 15.97C5.43 16.57 5.7 17.17 6 17.7C7.08 19.43 8.95 " +
            "20.67 10.96 20.92C13.1 21.19 15.39 20.8 17.03 19.32C18.86 17.66 19.5 15 18.56 12.72L18.43 " +
            "12.46C18.22 12 17.66 11.2 17.66 11.2M14.5 17.5C14.22 17.74 13.76 18 13.4 18.1C12.28 18.5 " +
            "11.16 17.94 10.5 17.28C11.69 17 12.4 16.12 12.61 15.23C12.78 14.43 12.46 13.77 12.33 " +
            "13C12.21 12.26 12.23 11.63 12.5 10.94C12.69 11.32 12.89 11.7 13.13 12C13.9 13 15.11 13.44 " +
            "15.37 14.8C15.41 14.94 15.43 15.08 15.43 15.23C15.46 16.05 15.1 16.95 14.5 17.5H14.5Z"
    )

    /**
     * MDI `bottle-tonic-skull` : la silhouette pleine du flacon tient à la taille d'une face,
     * là où un symbole biohazard n'y est plus qu'une tache.
     */
    val poison = glyphFromPathData(
        "M19 13V22H5V13C5 10.24 7.24 8 10 8V6H9V5H15V6H14V8C16.76 8 19 10.24 19 13M13 4L14 2H10L11 " +
            "4H13M12 11C9.79 11 8 12.79 8 15C8 16 8.39 16.9 9 17.59V19H10.25V17.5H11.38V19H12.63V17.5H13.75" +
            "V19H15V17.59C15.61 16.9 16 16 16 15C16 12.79 14.21 11 12 11M10.5 15C9.95 15 9.5 14.55 9.5 " +
            "14S9.95 13 10.5 13 11.5 13.45 11.5 14 11.05 15 10.5 15M11.25 16.25L12 15L12.75 16.25H11.25M13.5 " +
            "15C12.95 15 12.5 14.55 12.5 14S12.95 13 13.5 13 14.5 13.45 14.5 14 14.05 15 13.5 15Z"
    )
}

/**
 * Recadre un `pathData` SVG dans [-1, 1]² : chaque icône est centrée et mise à l'échelle sur
 * ses propres bornes, plutôt que sur le cadre 24×24 dont elle n'occupe jamais tout l'espace.
 */
private fun glyphFromPathData(pathData: String, margin: Float = 0.94f): OneFaceGlyph {
    val path = PathParser().parsePathString(pathData).toPath()
    val bounds = path.getBounds()
    val extent = maxOf(bounds.width, bounds.height)
    if (extent <= 0f) return OneFaceGlyph(path)
    val scale = 2f * margin / extent
    // Convention Compose : vecteurs-lignes, p' = p·M — la ligne 3 porte la translation.
    path.transform(
        Matrix().apply {
            this[0, 0] = scale
            this[1, 1] = scale
            this[3, 0] = -bounds.center.x * scale
            this[3, 1] = -bounds.center.y * scale
        }
    )
    return OneFaceGlyph(path)
}
